import { useEffect } from 'react';

const ORIGIN = 'https://actualsold.com';

export type DocumentHead = {
    title: string;
    description?: string;
    /** Path starting with `/`, becomes the absolute canonical + og:url. */
    canonicalPath?: string;
    noindex?: boolean;
};

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string): void {
    let el = document.head.querySelector<HTMLMetaElement>(selector);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.content = content;
}

function setLink(rel: string, href: string): void {
    let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        document.head.appendChild(el);
    }
    el.href = href;
}

/**
 * Imperative head update. Split from the hook so tests can exercise it without
 * a rendering helper package.
 */
export function applyDocumentHead({ title, description, canonicalPath, noindex }: DocumentHead): void {
    document.title = title;
    if (description) {
        setMeta('meta[name="description"]', 'name', 'description', description);
        setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    }
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);

    if (canonicalPath) {
        const url = `${ORIGIN}${canonicalPath === '/' ? '/' : canonicalPath}`;
        setLink('canonical', url);
        setMeta('meta[property="og:url"]', 'property', 'og:url', url);
    }

    const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (noindex) {
        setMeta('meta[name="robots"]', 'name', 'robots', 'noindex, nofollow');
    } else {
        robots?.remove();
    }
}

/**
 * Client-side head. Crawlers that execute JS pick this up; the static
 * index.html head is the floor for those that don't, which is why it describes
 * the landing page rather than the console.
 */
export function useDocumentHead(head: DocumentHead): void {
    useEffect(() => {
        applyDocumentHead(head);
    }, [head.title, head.description, head.canonicalPath, head.noindex]);
}
